import { Avatar, Badge, Box, Card, Grid, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { convertCurrencyAmountRange, getCurrencySymbol, hasPermission, isTitleToShow } from '../../utils';
import { Language, LocationOn, LockOutlined } from '@mui/icons-material';
import indiaFlag from '../../assets/india_flag.png'
import avatar from '../../assets/avatar.jpg'
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GradientIcon from '../../ui-components/icons/GradientIcon';
import { User, Users, Plus, ChevronRight } from 'lucide-react';
import { Secondary } from '../../ui-components/Buttons';
import CategoriesIcon from '../../ui-components/icons/CategoriesIcon'
import StagesIcon from '../../ui-components/icons/StagesIcon'
import SectorsIcon from '../../ui-components/icons/SectorsIcon'
import ChequeSizeIcon from '../../ui-components/icons/ChequeSizeIcon'
import BusinessModelsIcon from '../../ui-components/icons/BusinessModelsIcon'
import EditPencilIcon from '../../ui-components/icons/EditPencilIcon'
import EmailIcon from '../../ui-components/icons/EmailIcon'
import OfficeContactIcon from '../../ui-components/icons/OfficeContactIcon'
import StartupsInvestedIcon from '../../ui-components/icons/StartupsInvestedIcon'
import ContactMailIcon from '../../ui-components/icons/ContactMailIcon'
import AlertDialog from 'ui-components/AlertPopup';
import { useSelector } from 'react-redux';

// Shared visual tokens for the profile cards (indigo accent, soft borders, subtle elevation)
const CARD_RADIUS = "16px";

// ── Single knob for every indigo/blue outline (Edit buttons, header pills, icon badges) ──
// Darkness: raise the alpha (last number, 0–1) to make it darker, lower it to fade.
// Thickness: change INDIGO_BORDER_WIDTH.
const INDIGO_BORDER_COLOR = "rgba(68, 76, 231, 0.55)";
const INDIGO_BORDER_WIDTH = "1.5px";
const INDIGO_BORDER = `${INDIGO_BORDER_WIDTH} solid ${INDIGO_BORDER_COLOR}`;
// The accent must stay indigo. It used to read theme.customColors.indigo600, but
// the shared theme now sets indigo600/indigo700/b300 to `black` (to match
// startup-web), which turned every Edit label, pill and icon chip black.
const INDIGO_ACCENT = "#444CE7";

const CardHeader = ({ title, action = null, theme }) => {
    const userPermissions = useSelector((state) => state?.permissions?.userPermissions);
    const indigo = INDIGO_ACCENT;
    return <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb={"1.25rem"}>
        <Typography component={"h2"} fontWeight={700} fontSize={"1.125rem"} lineHeight={1.2} color={theme?.palette?.customColors?.g300 || "#101828"}>
            {title}
        </Typography>
        {action && hasPermission("profile", "edit", userPermissions) && <IconButton
            aria-label={title ? `Edit ${title}` : "Edit"}
            onClick={action}
            sx={{
                // Icon-only pencil, copied from startup-web's ViewDetails EditButton: a
                // borderless IconButton with no "Edit" label. EditPencilIcon strokes with
                // currentColor, so the button's colour drives it (black family, matching
                // startup-web's black pencil).
            }}
        >
            <EditPencilIcon />
        </IconButton>}
    </Box>
}

const RichTextDisplay = ({ htmlContent, color = "", textAlign = "start" }) => {
    const [open, setOpen] = useState(false);

    let content = open ? htmlContent : htmlContent?.substring(0, 300);
    return (
        <>
            {content && content?.length > 0 ?
                <div className='row rich-text'>
                    <div dangerouslySetInnerHTML={{ __html: content }} className='row rich-text' style={{ color: color, textAlign: textAlign }} />
                    {
                        htmlContent?.length > 300 && <Typography display={"flex"} variant='title' sx={{ fontSize: "0.8rem", textAlign: 'center', lineHeight: 1, justifyContent: 'flex-end', marginRight: "1rem", cursor: 'pointer' }} onClick={() => setOpen(!open)}>{!open ? '...more' : 'See less'}</Typography>
                    }
                </div>
                : "-"
            }
        </>
    );
};

// Names the still-empty fields in the "Add role, bio & LinkedIn" prompt.
const formatMissingList = (items) => {
    if (items.length <= 1) return items[0] || '';
    if (items.length === 2) return `${items[0]} & ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
};

// Partner row, styled to match startup-web's Founders card (PersonDetails):
// avatar with a LinkedIn badge, name, uppercase role, clamped bio, and an
// "Add role, bio & LinkedIn" prompt for whatever is still missing. Reads the
// investor's existing partner fields (image_url / name / title|type / bio / linkedin_url).
// ---- Empty contact slot ----------------------------------------------------
// Copied verbatim from the senior's startup-web implementation
// (views/profile/profileSettings/ViewDetails.js) so both profiles read the same:
// a dashed "add" slot, visibly distinct from the solid chips beside it, with the
// verb always visible rather than revealed on hover. Presentation only - the
// caller supplies onAdd, so this stays free of investor-web's routing.
const AddContact = ({ theme, icon, label, onAdd }) => (
    <Box
        component="button"
        type="button"
        onClick={onAdd}
        aria-label={`Add ${label}`}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            borderRadius: '10px',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.6,
            color: theme.palette.customColors.indigo600,
            backgroundColor: 'white',
            border: `1px dashed ${theme.palette.customColors.indigo600}`,
            transition: 'background-color 0.15s ease',
            '& svg': { color: theme.palette.customColors.indigo600 },
            '&:hover': {
                backgroundImage:
                    'linear-gradient(#F5F5F5, #F5F5F5), linear-gradient(135deg, #7852F8 0%, #84AEF6 100%)',
                boxShadow: 'none',
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.customColors.indigo600}`,
                outlineOffset: '2px',
            },
        }}
    >
        <Plus size={18} strokeWidth={2} />
        {icon}
        Add {label}
    </Box>
);

const PersonDetails = ({ theme, data, canEdit = false, onAdd }) => {
    const hasLinkedin = Boolean(data?.linkedin_url);
    const openLinkedin = () => {
        if (hasLinkedin) {
            const url = String(data?.linkedin_url);
            window.open(url.startsWith('http') ? url : 'https://' + url, '_blank', 'noopener,noreferrer');
        }
    };
    const isImageUploaded = data?.image_url && data?.image_url !== 'undefined';
    const imageSrc = isImageUploaded ? data?.image_url + `?cacheBuster=${new Date().getTime()}` : undefined;
    const role = data?.title || data?.type;
    const missingDetails = [
        !role && 'role',
        !data?.bio && 'bio',
        !hasLinkedin && 'LinkedIn',
    ].filter(Boolean);

    const gray300 = theme.palette.customColors.gray300 || '#EAECEF';

    return (
        <Box
            onClick={hasLinkedin ? openLinkedin : undefined}
            sx={{
                width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'row',
                alignItems: 'flex-start', gap: '1rem', padding: '1rem',
                border: `1px solid ${gray300}`, borderRadius: '12px',
                cursor: hasLinkedin ? 'pointer' : 'default', transition: 'border-color 0.15s ease',
                '&:hover': hasLinkedin ? { borderColor: theme.palette.customColors.indigo600 } : {},
            }}
        >
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={hasLinkedin ? (
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(16,24,40,0.18)' }}>
                        <LinkedInIcon sx={{ fontSize: 16, color: INDIGO_ACCENT }} />
                    </Box>
                ) : null}
            >
                <Avatar
                    src={imageSrc}
                    alt={data?.name || 'Partner'}
                    sx={{ width: 56, height: 56, border: `1px solid ${gray300}`, ...(isImageUploaded ? {} : { backgroundColor: 'white', color: 'black' }) }}
                >
                    <GradientIcon icon={User} size={24} />
                </Avatar>
            </Badge>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                {data?.name && <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 }}>{data?.name}</Typography>}
                {role && <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: theme.palette.customColors.g300, mt: '0.2rem' }}>{role}</Typography>}
                {data?.bio && <Typography sx={{ fontSize: '0.9rem', color: theme.palette.customColors.g200, mt: '0.4rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{data?.bio}</Typography>}
                {canEdit && missingDetails.length > 0 && (
                    <Box component="button" type="button"
                        onClick={(e) => { e.stopPropagation(); onAdd?.(); }}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', mt: '0.5rem', padding: 0, border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', fontSize: '0.85rem', fontWeight: 500, textAlign: 'left', color: INDIGO_ACCENT, '&:hover': { textDecoration: 'underline' } }}
                    >
                        <Plus size={16} strokeWidth={2} />
                        Add {formatMissingList(missingDetails)}
                    </Box>
                )}
            </Box>

            {hasLinkedin && <ChevronRight size={20} style={{ color: theme.palette.customColors.g100, flexShrink: 0, alignSelf: 'flex-start', marginTop: '0.15rem' }} />}
        </Box>
    );
}

const PersonalDetails = ({ data: personalDetails, avatarList: avatarList, setLoading = () => { } }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const userAccess = useSelector((state) => state?.permissions);
    const investor_types = useSelector((state) => state?.investorDetails?.investor_types);

    const [name, setName] = useState(null)
    const [website, setWebsite] = useState(null)
    const [profileImage, setProfileImage] = useState(null)
    const [sectors, setSectors] = useState([])
    const [businessModel, setBusinessModel] = useState([])

    const [alertImage, setAlertImage] = useState(null)
    const [showAlert, setShowAlert] = useState(false)
    const [alertMessage, setAlertMessage] = useState(null)
    const [alertHeading, setAlertHeading] = useState(null)

    const [shiftSteps, setShiftSteps] = useState(false);
    const [showInitialLogo, setShowInitialLogo] = useState(false)

    useEffect(() => {
        if (personalDetails != null) {
            let configArray = personalDetails?.sector_info?.map(item => {
                return item.config_values?.map(sector => {
                    return sector.sub_category
                })
            })
            setSectors(configArray.flat(1))

            let configArrayBusinessModel = personalDetails?.business_model_info?.map(model => {
                return model.name
            })
            setBusinessModel(configArrayBusinessModel.flat(1))
        }
    }, [personalDetails])

    useEffect(() => {
        setShiftSteps(personalDetails?.investor_type ? String(personalDetails?.investor_type)?.toLowerCase()?.includes("angel") : true)
        if (personalDetails?.investor_type) {
            if (String(personalDetails?.investor_type)?.toLowerCase()?.includes("angel")) {
                setName(personalDetails?.first_name + " " + personalDetails?.last_name)
                setProfileImage(personalDetails?.profile_path)
                setWebsite(null)
            } else {
                setName(personalDetails?.organization_name)
                setProfileImage(personalDetails?.website_logo)
                setWebsite(personalDetails?.organization_website)
            }
        } else {
            setName(personalDetails?.first_name + " " + personalDetails?.last_name)
            setWebsite(null)
            setProfileImage(personalDetails?.profile_path)
        }
    }, [personalDetails?.investor_type, personalDetails])

    const getAvatarProps = (name) => {
        const initials = name
            ? name
                ?.split(' ')
                ?.map((n) => n[0])
                ?.join('')
                ?.toUpperCase()
            : '';
        return {
            sx: {
                bgcolor: '#F9F5FF',
                color: '#3538CD',
                width: "100%",
                height: "100%",
                fontSize: "3rem",
                fontWeight: 500,
                borderRadius: "14px",
            },
            children: (initials)?.slice(0, 2),
        };
    };

    // ---- theme tokens ----
    // gray200 (#EAECF0) is the light neutral border; g75 (#a1a3a7) was too heavy for card outlines.
    // mutedText: no theme grey matches the design's blue-grey secondary (#667085), so it stays literal.
    const indigo = INDIGO_ACCENT;
    const borderColor = theme?.palette?.customColors?.gray200 || "#EAECF0";
    const mutedText = "#667085";
    const headingText = theme?.palette?.customColors?.g300 || "#101828";

    const cardStyles = {
        padding: { xs: "1.25rem", md: "1.5rem" },
        boxShadow: "0px 1px 3px rgba(16,24,40,0.06)",
        backgroundColor: theme?.palette?.customColors?.whiteBG || "#ffffff",
        border: `1px solid ${borderColor}`,
        borderRadius: CARD_RADIUS,
        overflow: "visible",
    };

    // Icon chips share the same indigo outline knob (INDIGO_BORDER at top of file).
    // Default: no fill (just the outline). On hover: light-indigo fill (indigo50).
    const iconBadgeBg = "transparent";
    const iconBadgeHoverBg = theme?.palette?.customColors?.indigo50 || "#EEF4FF";

    const focusIconBadge = {
        width: 48,
        height: 48,
        borderRadius: "10px",
        backgroundColor: iconBadgeBg,
        border: INDIGO_BORDER,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: iconBadgeHoverBg },
    };

    const rowIconBadge = {
        width: 34,
        height: 34,
        borderRadius: "8px",
        backgroundColor: iconBadgeBg,
        border: INDIGO_BORDER,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: iconBadgeHoverBg },
        "& svg": { fontSize: 18, color: indigo },
    };

    const pillStyles = {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        // Indigo-tinted outline (shared knob — see INDIGO_BORDER at top of file)
        border: INDIGO_BORDER,
        borderRadius: "8px",
        padding: "0.5rem 0.75rem",
        backgroundColor: theme?.palette?.customColors?.whiteBG || "#ffffff",
        color: headingText,
        fontSize: "0.8125rem",
        fontWeight: 600,
        cursor: "pointer",
        maxWidth: "100%",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: theme?.palette?.customColors?.indigo50 || "#EEF4FF" },
    };

    // Truncate a list to "a, b + N" and open the full list in the AlertDialog on click
    const openFocusAlert = (heading, image, message) => {
        setAlertImage(image);
        setAlertHeading(heading);
        setAlertMessage(message);
        setShowAlert(true);
    };

    const chequeSize = `${getCurrencySymbol(personalDetails?.investment_amount_type)}${convertCurrencyAmountRange(personalDetails?.min, personalDetails?.investment_amount_type, 0)} TO ${getCurrencySymbol(personalDetails?.investment_amount_type)}${convertCurrencyAmountRange(personalDetails?.max, personalDetails?.investment_amount_type, 0)}`;

    const categoryNames = personalDetails?.category_info?.map(item => item?.name) || [];
    const stageNames = personalDetails?.stage_info?.map(item => item?.name) || [];
    const isSectorAgnostic = personalDetails?.sector_type === "All";
    const isAllBusinessModels = personalDetails?.business_model_type === "All";

    const focusItems = [
        {
            key: "categories",
            label: "Categories",
            icon: CategoriesIcon,
            values: categoryNames,
            fullText: categoryNames.join(", "),
            emptyText: "-",
        },
        {
            key: "stages",
            label: "Stages",
            icon: StagesIcon,
            values: stageNames,
            fullText: stageNames.join(", "),
            emptyText: "-",
        },
        {
            key: "sectors",
            label: "Sectors",
            icon: SectorsIcon,
            values: isSectorAgnostic ? [] : sectors,
            fixedText: isSectorAgnostic ? "Sector Agnostic" : null,
            fullText: isSectorAgnostic ? "Sector Agnostic" : (sectors || []).join(", "),
            emptyText: "-",
        },
        {
            key: "cheque",
            label: "Cheque Size",
            icon: ChequeSizeIcon,
            fixedText: chequeSize,
            fullText: chequeSize,
            values: [],
        },
        {
            key: "business_models",
            label: "Business Models",
            icon: BusinessModelsIcon,
            values: isAllBusinessModels ? [] : businessModel,
            fixedText: isAllBusinessModels ? "All" : null,
            fullText: isAllBusinessModels ? "All" : (businessModel || []).join(", "),
            emptyText: "-",
        },
    ];

    // `align` follows the layout: "center" for the desktop tile, "left" for the mobile
    // row. Defaulted so any other caller keeps the original centred behaviour.
    const renderFocusValue = (item, align = "center") => {
        if (item.fixedText != null) {
            return <Typography sx={{ fontSize: "0.8rem", color: mutedText, lineHeight: 1.4, textAlign: align }}>{item.fixedText}</Typography>;
        }
        const list = item.values || [];
        if (list.length === 0) {
            return <Typography sx={{ fontSize: "0.8rem", color: mutedText, lineHeight: 1.4, textAlign: align }}>{item.emptyText}</Typography>;
        }
        const shown = list.slice(0, 2).join(", ");
        const remaining = list.length - 2;
        return (
            <Typography
                onClick={remaining > 0 ? () => openFocusAlert(item.label, typeof item.icon === "function" ? null : item.icon, item.fullText) : undefined}
                sx={{ fontSize: "0.8rem", color: mutedText, lineHeight: 1.4, textAlign: align, cursor: remaining > 0 ? "pointer" : "default" }}
            >
                {shown}{remaining > 0 ? <Box component="span" sx={{ color: indigo, fontWeight: 600 }}>{`.. + ${remaining}`}</Box> : null}
            </Typography>
        );
    };

    // Location string (city, state, country) — matches the existing precedence
    const locationString = [personalDetails?.city_name, personalDetails?.state_name, personalDetails?.country_name].filter(Boolean).join(", ");
    const investorTypeLabel = investor_types?.find(item => item?.value === personalDetails?.investor_type)?.label || "";
    const displayTitle = personalDetails?.title && String(personalDetails?.investor_type)?.toLowerCase()?.includes("angel") && isTitleToShow(personalDetails?.title) ? personalDetails?.title : "";

    const otherInfoRows = [
        { icon: <EmailIcon />, selfFramed: true, label: "Email", value: personalDetails?.email || "-" },
        { icon: <OfficeContactIcon />, selfFramed: true, label: "Office Contact No.", value: personalDetails?.phone_number ? `${personalDetails?.country_code}-${personalDetails?.phone_number}` : "-" },
        { icon: <StartupsInvestedIcon />, selfFramed: true, label: "No. of Startups Invested In", value: personalDetails?.investment_count || 0 },
    ];

    const hasPartners = !(String(personalDetails?.investor_type)?.toLowerCase()?.includes("angel")) && (personalDetails?.partners?.length > 0 && personalDetails?.partners[0]?.name?.trim()?.length > 0);

    return (
        <Box sx={{ maxWidth: "1040px", marginInline: "auto", width: "100%", display: "flex", flexDirection: "column", gap: "1.25rem", paddingBottom: "2rem", position: "relative" }}>

            {/* ---------- Header ---------- */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "center", sm: "flex-start" }, gap: { xs: "1.25rem", sm: "1.75rem" }, padding: { xs: "0.5rem 0.5rem 0.25rem", sm: "0.75rem 0.5rem 0.25rem" } }}>
                {/* Avatar */}
                <Box sx={{ position: "relative", width: { xs: "7.5rem", sm: "8rem" }, height: { xs: "7.5rem", sm: "8rem" }, flexShrink: 0, borderRadius: "16px", border: `1px solid ${theme.palette.customColors.gray300 || borderColor}` }}>
                    <IconButton
                        // Same pencil as the section-header Edit buttons (EditPencilIcon),
                        // slimmed to match: thinner border + less padding. Keeps a white disc
                        // so it stays visible floating on the photo.
                        sx={{ position: 'absolute', top: 0, right: 0, transform: 'translate(35%, -35%)', background: "white !important", border: "1px solid #d0d5dd", padding: "4px", zIndex: 1, "& svg": { width: 14, height: 14 } }}
                        onClick={() => { hasPermission("profile", "edit", userAccess?.userPermissions) && navigate(`/profile/edit_profile?page=0`); }}
                    >
                        <EditPencilIcon />
                    </IconButton>
                    {!showInitialLogo ? (
                        <img
                            src={profileImage ? profileImage + `?cacheBuster=${new Date().getTime()}` : ""}
                            onLoad={() => { setShowInitialLogo(false) }}
                            onError={() => { setShowInitialLogo(true) }}
                            style={{ width: "100%", height: "100%", borderRadius: "14px", display: "block", objectFit: "cover" }}
                        />
                    ) : (
                        <Avatar {...getAvatarProps(name)} onClick={() => { hasPermission("profile", "edit", userAccess?.userPermissions) && navigate(`/profile/edit_profile?page=0`); }} />
                    )}
                </Box>

                {/* Identity */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "0.4rem", alignItems: { xs: "center", sm: "flex-start" }, textAlign: { xs: "center", sm: "left" }, flex: 1, minWidth: 0, paddingTop: { sm: "0.125rem" } }}>
                    <Typography sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem" }, fontWeight: 700, lineHeight: 1.15, color: headingText }}>
                        {displayTitle ? `${displayTitle} ` : ""}{name}
                    </Typography>
                    {investorTypeLabel && <Typography sx={{ fontSize: "0.95rem", color: mutedText, fontWeight: 500 }}>{investorTypeLabel}</Typography>}
                    {locationString && <Box sx={{ display: "flex", alignItems: "center", gap: "0.375rem", color: mutedText }}>
                        <LocationOn sx={{ fontSize: 18, color: indigo }} />
                        <Typography sx={{ fontSize: "0.9rem", color: headingText }}>{locationString}</Typography>
                        {personalDetails?.country_name === "India" && <img src={indiaFlag} alt="" style={{ width: "1.25rem", marginLeft: "2px" }} />}
                    </Box>}

                    {/* Contact pills */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.625rem", marginTop: "0.375rem", justifyContent: { xs: "center", sm: "flex-start" } }}>
                        {/* Each pill now falls back to a dashed "Add X" slot instead of
                            vanishing, matching startup-web's profile. The add slot is gated
                            on edit permission - a viewer who cannot edit is shown nothing
                            rather than a button that would bounce off the edit route. */}
                        {website ? (
                            <Box sx={pillStyles} onClick={() => window.open(String(website)?.includes("http") ? website : "https://" + website)}>
                                <Language sx={{ fontSize: 18, color: indigo }} />
                                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "12rem" }}>{website}</Box>
                            </Box>
                        ) : (
                            hasPermission("profile", "edit", userAccess?.userPermissions) && (
                                <AddContact
                                    theme={theme}
                                    label="Website"
                                    icon={<Language sx={{ fontSize: 18 }} />}
                                    onAdd={() => navigate(`/profile/edit_profile?page=0`)}
                                />
                            )
                        )}
                        {personalDetails?.email ? (
                            <Box sx={pillStyles} onClick={() => window.open(`mailto:${personalDetails?.email}`)}>
                                <ContactMailIcon size={18} />
                                Email
                            </Box>
                        ) : (
                            hasPermission("profile", "edit", userAccess?.userPermissions) && (
                                <AddContact
                                    theme={theme}
                                    label="Email"
                                    icon={<ContactMailIcon size={18} />}
                                    onAdd={() => navigate(`/profile/edit_profile?page=0`)}
                                />
                            )
                        )}
                        {personalDetails?.profile_url ? (
                            <Box sx={pillStyles} onClick={() => window.open(String(personalDetails?.profile_url)?.includes("http") ? personalDetails?.profile_url : "https://" + personalDetails?.profile_url)}>
                                <LinkedInIcon sx={{ fontSize: 18, color: theme.palette.customColors.indigo600 }} />
                                LinkedIn
                            </Box>
                        ) : (
                            hasPermission("profile", "edit", userAccess?.userPermissions) && (
                                <AddContact
                                    theme={theme}
                                    label="LinkedIn"
                                    icon={<LinkedInIcon sx={{ fontSize: 18 }} />}
                                    onAdd={() => navigate(`/profile/edit_profile?page=0`)}
                                />
                            )
                        )}
                    </Box>
                </Box>
            </Box>

            {/* ---------- Overview ---------- */}
            <Card sx={cardStyles}>
                <CardHeader title={"Overview"} theme={theme} action={() => { navigate(`/profile/edit_profile?page=0`) }} />
                <Box sx={{ color: mutedText, fontSize: "0.9rem", lineHeight: 1.6 }}>
                    <RichTextDisplay htmlContent={personalDetails?.bio} textAlign='start' color={mutedText} />
                </Box>
            </Card>

            {/* ---------- Investment Focus ---------- */}
            <Card sx={cardStyles}>
                <CardHeader title={"Investment Focus"} theme={theme} action={() => { navigate(`/profile/edit_profile?page=${shiftSteps ? 1 : 2}`) }} />
                {/* Two layouts, one data source. Below sm each item is a full-width row -
                    icon left, label + value stacked to its right, left-aligned - because
                    the centred tile forced 2-per-row on a phone, which squeezed the value
                    text into 3-4 wrapped lines. From sm up it stays the centred tile grid. */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }, gap: { xs: "0.75rem", sm: "1rem" } }}>
                    {focusItems.map((item) => (
                        <Box
                            key={item.key}
                            sx={{
                                border: `1px solid ${borderColor}`,
                                borderRadius: "12px",
                                padding: { xs: "0.875rem", sm: "1.5rem 0.875rem 1rem" },
                                display: "flex",
                                flexDirection: { xs: "row", sm: "column" },
                                alignItems: "center",
                                textAlign: { xs: "left", sm: "center" },
                                gap: { xs: "0.875rem", sm: "0.75rem" },
                                minWidth: 0,
                            }}
                        >
                            {typeof item.icon === "function" ? (
                                // The Figma icon carries its own gradient-bordered tile, so it
                                // renders on its own - no focusIconBadge wrapper. Smaller on
                                // mobile so it reads as a leading icon, not the hero element.
                                <item.icon size={isSmallScreen ? 44 : 56} />
                            ) : (
                                <Box sx={focusIconBadge}>
                                    <img src={item.icon} alt="" style={{ width: 24, height: 24, objectFit: "contain" }} />
                                </Box>
                            )}
                            {/* On mobile this column holds label + value together to the right
                                of the icon; on desktop it is a passthrough so the original
                                stacking is untouched. */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "0.25rem", sm: "0.75rem" }, minWidth: 0, flex: 1, alignItems: { xs: "flex-start", sm: "center" }, width: "100%" }}>
                                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: headingText, lineHeight: 1.2 }}>{item.label}</Typography>
                                <Box sx={{ flex: { xs: "0 0 auto", sm: 1 }, display: "flex", alignItems: "flex-start", justifyContent: { xs: "flex-start", sm: "center" }, width: "100%", minWidth: 0 }}>
                                    {renderFocusValue(item, isSmallScreen ? "left" : "center")}
                                </Box>
                            </Box>
                            {/* Figma: 3px underline, brand gradient (done in CSS, not an SVG).
                                Desktop only - on a horizontal row it would sit under the icon
                                and read as an unrelated stray line. */}
                            {!isSmallScreen && (
                                <Box sx={{ height: "3px", width: "70%", background: "linear-gradient(90deg, #7852F8 0%, #84AEF6 100%)", borderRadius: "1px", marginTop: "0.5rem" }} />
                            )}
                        </Box>
                    ))}
                </Box>
            </Card>

            {/* ---------- Team (partners) ---------- */}
            {/* Partners/Team card - shown for every non-Angel investor (like startup-web's
                Founders card), with a matching empty state when no partners exist yet. */}
            {!shiftSteps && <Card sx={cardStyles}>
                {/* page=3 opens the Partners-only editor. Pencil only when partners exist -
                    the empty state's "Add Partner(s)" button handles the first add. */}
                <CardHeader title={"Team"} theme={theme} action={hasPartners ? () => navigate(`/profile/edit_profile?page=3`) : undefined} />
                {hasPartners ? (
                    // Stacked partner rows, styled like startup-web's Founders card.
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {personalDetails?.partners?.map((person, index) => (
                            <PersonDetails
                                theme={theme}
                                data={person}
                                canEdit={hasPermission("profile", "edit", userAccess?.userPermissions)}
                                onAdd={() => navigate(`/profile/edit_profile?page=3`)}
                                key={index}
                            />
                        ))}
                    </Box>
                ) : (
                    // Empty state, matching the Founders card's "No founders added yet".
                    <Box sx={{ display: 'grid', placeItems: 'center', gap: '0.75rem', minHeight: 180, padding: '1.5rem 1rem', textAlign: 'center', borderRadius: '12px', border: `1px dashed ${theme.palette.customColors.gray300 || '#EAECEF'}` }}>
                        <GradientIcon icon={Users} size={40} strokeWidth={1} />
                        <Box>
                            <Typography sx={{ fontWeight: 600, color: theme.palette.customColors.g300 }}>No partners added yet</Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: theme.palette.customColors.g100, mt: '0.25rem', maxWidth: 260 }}>
                                Add each partner&apos;s name, role, bio &amp; LinkedIn so others know who&apos;s on your team.
                            </Typography>
                        </Box>
                        {hasPermission("profile", "edit", userAccess?.userPermissions) && (
                            <Secondary sx={{ padding: '0.5rem 1rem', mt: '0.25rem' }} startIcon={<Plus size={16} strokeWidth={2} />} onClick={() => navigate(`/profile/edit_profile?page=3`)}>
                                Add Partner(s) Details
                            </Secondary>
                        )}
                    </Box>
                )}
            </Card>}

            {/* ---------- Other Information ---------- */}
            <Card sx={cardStyles}>
                <CardHeader title={"Other Information"} theme={theme} action={() => { navigate(`/profile/edit_profile?page=${shiftSteps ? 0 : 1}`) }} />
                <Box>
                    {otherInfoRows.map((row, index) => (
                        <Box key={index} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", paddingBlock: "0.875rem", borderTop: index === 0 ? "none" : `1px solid ${borderColor}` }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
                                {row.selfFramed ? row.icon : <Box sx={rowIconBadge}>{row.icon}</Box>}
                                <Typography sx={{ fontSize: "0.9rem", color: headingText }}>{row.label}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: "0.9rem", color: mutedText, textAlign: "right", wordBreak: "break-word" }}>{row.value}</Typography>
                        </Box>
                    ))}
                </Box>
            </Card>

            {/* Privacy reassurance. Static and always shown - the card above lists an
                email and a phone number, which a user can reasonably assume are on
                their public profile. Purely informational: no state, no API, no
                permission check. alignSelf centres it inside the column; marginBlock
                adds breathing room on top of the parent's 1.25rem gap. */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    alignSelf: "center",
                    marginBlock: "0.75rem",
                    paddingBlock: "0.5rem",
                    paddingInline: "0.875rem",
                    borderRadius: "10px",
                    backgroundColor: theme?.palette?.customColors?.indigo50 || "#EEF4FF",
                }}
            >
                <LockOutlined sx={{ fontSize: 15, color: indigo, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.8rem", color: mutedText, textAlign: "center" }}>
                    This information is private and is not publicly available.
                </Typography>
            </Box>

            <AlertDialog open={showAlert} handleClose={() => { setShowAlert(false); setAlertMessage(null); setAlertHeading(null); setAlertImage(null) }} heading={alertHeading} subheading={alertMessage} image={alertImage} />
        </Box>
    )
}

export default PersonalDetails
